import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, Plus, Star, ArrowDownCircle, ArrowUpCircle, AlertTriangle, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import UtentiTab from "@/components/UtentiTab";
import RuoliTab from "@/components/RuoliTab";
import useSort from "@/lib/useSort";
import { useHasPerm } from "@/components/PermissionGate";

// -------------------- Depositi --------------------
function DepositiTab() {
  const canEdit = useHasPerm()("impostazioni:edit");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: "", indirizzo: "", preferito: false });
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/deposits");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ nome: "", indirizzo: "", preferito: false }); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ nome: d.nome, indirizzo: d.indirizzo || "", preferito: !!d.preferito }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome) return toast.error("Nome obbligatorio");
    try {
      if (editing) await api.put(`/deposits/${editing.id}`, form);
      else await api.post("/deposits", form);
      toast.success("Salvato");
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const togglePref = async (d) => {
    try {
      await api.put(`/deposits/${d.id}`, { ...d, preferito: !d.preferito });
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/deposits/${deleteId}`);
      toast.success("Eliminato");
      setDeleteId(null);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
          I depositi sono i magazzini dove sono custoditi i tuoi prodotti.
        </p>
        {canEdit && (
          <Button onClick={openCreate} className="ds-btn-primary" data-testid="deposit-add">
            <Plus size={16} className="mr-1.5" /> Nuovo deposito
          </Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12"></TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Indirizzo</TableHead>
            <TableHead className="text-right">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessun deposito</TableCell></TableRow>}
          {list.map((d) => (
            <TableRow key={d.id} data-testid={`deposit-row-${d.id}`}>
              <TableCell>
                {canEdit ? (
                  <button onClick={() => togglePref(d)} aria-label="preferito" data-testid={`deposit-fav-${d.id}`}>
                    <Star size={18} fill={d.preferito ? "#D9A05B" : "transparent"} stroke={d.preferito ? "#D9A05B" : "#8A7358"} />
                  </button>
                ) : (
                  <Star size={18} fill={d.preferito ? "#D9A05B" : "transparent"} stroke={d.preferito ? "#D9A05B" : "#8A7358"} />
                )}
              </TableCell>
              <TableCell className="font-medium">{d.nome}</TableCell>
              <TableCell className="text-sm">{d.indirizzo || "—"}</TableCell>
              <TableCell className="text-right">
                {canEdit && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(d.id)}><Trash2 size={14} style={{ color: "var(--ds-danger)" }} /></Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">{editing ? "Modifica deposito" : "Nuovo deposito"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <Label className="label-tag">Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="deposit-form-nome" />
            </div>
            <div>
              <Label className="label-tag">Indirizzo</Label>
              <Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} className="mt-1.5 rounded-lg" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.preferito} onChange={(e) => setForm({ ...form, preferito: e.target.checked })} />
              Imposta come preferito
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="deposit-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il deposito?</AlertDialogTitle>
            <AlertDialogDescription>Operazione irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} style={{ background: "var(--ds-danger)" }}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// -------------------- Unità di misura --------------------
function UnitsTab() {
  const canEdit = useHasPerm()("impostazioni:edit");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nome, setNome] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => { const { data } = await api.get("/units"); setList(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!nome) return toast.error("Nome obbligatorio");
    try {
      if (editing) await api.put(`/units/${editing.id}`, { nome });
      else await api.post("/units", { nome });
      toast.success("Salvato");
      setOpen(false); setNome(""); setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>Elenco tipi di unità (es. pezzo, kg, litro…)</p>
        {canEdit && (
          <Button onClick={() => { setEditing(null); setNome(""); setOpen(true); }} className="ds-btn-primary" data-testid="unit-add">
            <Plus size={16} className="mr-1.5" /> Nuova unità
          </Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead className="text-right">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 && <TableRow><TableCell colSpan={2} className="text-center py-6 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessuna unità</TableCell></TableRow>}
          {list.map((u) => (
            <TableRow key={u.id} data-testid={`unit-row-${u.id}`}>
              <TableCell className="font-medium">{u.nome}</TableCell>
              <TableCell className="text-right">
                {canEdit && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(u); setNome(u.nome); setOpen(true); }}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(u.id)}><Trash2 size={14} style={{ color: "var(--ds-danger)" }} /></Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">{editing ? "Modifica unità" : "Nuova unità"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <Label className="label-tag">Nome *</Label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} className="mt-1.5 rounded-lg" required data-testid="unit-form-nome" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="unit-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'unità?</AlertDialogTitle>
            <AlertDialogDescription>Non sarà possibile se è utilizzata da prodotti.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={async () => {
              try {
                await api.delete(`/units/${deleteId}`);
                toast.success("Eliminato"); setDeleteId(null); await load();
              } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
            }} style={{ background: "var(--ds-danger)" }}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// -------------------- Categorie --------------------
function CategoriesTab() {
  const canEdit = useHasPerm()("impostazioni:edit");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: "", categoria_padre_id: null });
  const [deleteId, setDeleteId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdateBusy, setBulkUpdateBusy] = useState(false);
  const [bulkUpdateResult, setBulkUpdateResult] = useState(null);

  const load = async () => { const { data } = await api.get("/categories"); setList(data); };
  useEffect(() => { load(); }, []);

  const parents = useMemo(() => list.filter((c) => !c.categoria_padre_id), [list]);
  const byParent = useMemo(() => {
    const map = {};
    list.forEach((c) => {
      if (c.categoria_padre_id) {
        if (!map[c.categoria_padre_id]) map[c.categoria_padre_id] = [];
        map[c.categoria_padre_id].push(c);
      }
    });
    return map;
  }, [list]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome) return toast.error("Nome obbligatorio");
    try {
      const payload = { nome: form.nome, categoria_padre_id: form.categoria_padre_id || null };
      if (editing) await api.put(`/categories/${editing.id}`, payload);
      else await api.post("/categories", payload);
      toast.success("Salvato");
      setOpen(false); setEditing(null); setForm({ nome: "", categoria_padre_id: null });
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openEdit = (c) => { setEditing(c); setForm({ nome: c.nome, categoria_padre_id: c.categoria_padre_id }); setOpen(true); };

  const authedDownload = async (path, fileName) => {
    try {
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Errore download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(err.message); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/categories/import`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setImportResult(data);
      toast.success(`${data.created} categorie create`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} righe saltate`);
      await load();
    } catch (err) { toast.error(typeof err.message === "string" ? err.message : "Errore import"); }
    finally { setImportBusy(false); e.target.value = ""; }
  };

  const handleBulkUpdate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUpdateBusy(true); setBulkUpdateResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/categories/bulk-update`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setBulkUpdateResult(data);
      toast.success(`${data.updated} categorie aggiornate`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} righe saltate`);
      await load();
    } catch (err) { toast.error(typeof err.message === "string" ? err.message : "Errore aggiornamento"); }
    finally { setBulkUpdateBusy(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <p className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
          Crea categorie padre (es. Acqua, Amari, Aperitivi) e relative sottocategorie.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="rounded-lg" onClick={() => authedDownload("/categories/export", "categorie_export.xlsx")} data-testid="categorie-export-button">
            <Download size={14} className="mr-1.5" /> Esporta XLS
          </Button>
          {canEdit && (
            <Button variant="outline" className="rounded-lg" onClick={() => { setBulkUpdateOpen(true); setBulkUpdateResult(null); }} data-testid="categorie-bulk-update-button">
              <Upload size={14} className="mr-1.5" /> Modifica massiva
            </Button>
          )}
          <Button variant="outline" className="rounded-lg" onClick={() => authedDownload("/categories/template", "template_categorie.xlsx")} data-testid="categorie-template-button">
            <Download size={14} className="mr-1.5" /> Template
          </Button>
          {canEdit && (
            <Button variant="outline" className="rounded-lg" onClick={() => { setImportOpen(true); setImportResult(null); }} data-testid="categorie-import-button">
              <Upload size={14} className="mr-1.5" /> Importa XLS
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => { setEditing(null); setForm({ nome: "", categoria_padre_id: null }); setOpen(true); }} className="ds-btn-primary" data-testid="category-add">
              <Plus size={16} className="mr-1.5" /> Nuova categoria
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {parents.length === 0 && <div className="text-sm text-center py-8" style={{ color: "var(--ds-text-secondary)" }}>Nessuna categoria</div>}
        {parents.map((p) => (
          <div key={p.id} className="ds-card p-4" data-testid={`category-parent-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-primary font-medium text-base">{p.nome}</div>
              {canEdit && (
                <div>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteId(p.id)}><Trash2 size={14} style={{ color: "var(--ds-danger)" }} /></Button>
                </div>
              )}
            </div>
            {(byParent[p.id] || []).length > 0 && (
              <div className="pl-4 mt-2 space-y-1 border-l-2" style={{ borderColor: "var(--ds-border)" }}>
                {(byParent[p.id] || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm pl-3 py-1.5">
                    <span style={{ color: "var(--ds-text-secondary)" }}>↳ {c.nome}</span>
                    {canEdit && (
                      <div>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)}><Pencil size={12} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteId(c.id)}><Trash2 size={12} style={{ color: "var(--ds-danger)" }} /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">{editing ? "Modifica categoria" : "Nuova categoria"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <Label className="label-tag">Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="category-form-nome" />
            </div>
            <div>
              <Label className="label-tag">Categoria padre</Label>
              <Select value={form.categoria_padre_id || "none"} onValueChange={(v) => setForm({ ...form, categoria_padre_id: v === "none" ? null : v })}>
                <SelectTrigger className="mt-1.5 rounded-lg">
                  <SelectValue placeholder="Nessuna (è una categoria padre)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna (categoria padre)</SelectItem>
                  {parents.filter((p) => p.id !== editing?.id).map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="category-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la categoria?</AlertDialogTitle>
            <AlertDialogDescription>Non sarà possibile se ha sottocategorie o prodotti associati.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={async () => {
              try {
                await api.delete(`/categories/${deleteId}`);
                toast.success("Eliminato"); setDeleteId(null); await load();
              } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
            }} style={{ background: "var(--ds-danger)" }}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="categorie-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">Importa categorie da XLS</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
              Colonne: <b>nome</b> (obbligatoria), <b>categoria_padre</b> (opzionale — nome della categoria padre).
              Le categorie già presenti vengono saltate. Indica prima le padri e poi le figlie (l'ordine sul file non conta: vengono comunque processate prima le padri).
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="rounded-lg" onClick={() => authedDownload("/categories/template", "template_categorie.xlsx")}>
                <Download size={14} className="mr-1.5" /> Scarica template
              </Button>
              <label className="ds-btn-primary inline-flex items-center cursor-pointer rounded-lg px-4 py-2 text-sm" style={{ background: "var(--ds-primary)", color: "white" }}>
                <Upload size={14} className="mr-1.5" />
                {importBusy ? "Caricamento…" : "Scegli file .xlsx"}
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} disabled={importBusy} data-testid="categorie-import-input" />
              </label>
            </div>
            {importResult && (
              <div className="space-y-2">
                <div className="text-sm">
                  <b style={{ color: "#7B8B6F" }}>Creati: {importResult.created}</b>
                  {" · "}
                  <b style={{ color: importResult.skipped?.length ? "var(--ds-danger)" : "inherit" }}>
                    Saltati: {importResult.skipped?.length || 0}
                  </b>
                </div>
                {importResult.skipped?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto text-xs p-3 rounded-lg border" style={{ borderColor: "var(--ds-border)" }}>
                    {importResult.skipped.map((s, i) => (
                      <div key={`cat-imp-${s.row || i}`} className="py-1 border-b last:border-b-0" style={{ borderColor: "var(--ds-border)" }}>
                        <b>Riga {s.row}:</b> {s.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-update dialog */}
      <Dialog open={bulkUpdateOpen} onOpenChange={setBulkUpdateOpen}>
        <DialogContent className="max-w-2xl" data-testid="categorie-bulk-update-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">Modifica massiva categorie</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
              Esporta l'elenco categorie, modificale sul foglio Excel (colonne: <b>nome</b>, <b>categoria_padre</b>) mantenendo la colonna <b>id</b>, poi reimporta.
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" className="rounded-lg" onClick={() => authedDownload("/categories/export", "categorie_export.xlsx")}>
                <Download size={14} className="mr-1.5" /> Esporta categorie
              </Button>
              <label className="ds-btn-primary inline-flex items-center cursor-pointer rounded-lg px-4 py-2 text-sm" style={{ background: "var(--ds-primary)", color: "white" }}>
                <Upload size={14} className="mr-1.5" />
                {bulkUpdateBusy ? "Aggiornamento…" : "Scegli file modificato"}
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpdate} disabled={bulkUpdateBusy} data-testid="categorie-bulk-update-input" />
              </label>
            </div>
            {bulkUpdateResult && (
              <div className="space-y-2">
                <div className="text-sm">
                  <b style={{ color: "#7B8B6F" }}>Aggiornate: {bulkUpdateResult.updated}</b>
                  {" · "}
                  <b style={{ color: bulkUpdateResult.skipped?.length ? "var(--ds-danger)" : "inherit" }}>
                    Saltate: {bulkUpdateResult.skipped?.length || 0}
                  </b>
                </div>
                {bulkUpdateResult.skipped?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto text-xs p-3 rounded-lg border" style={{ borderColor: "var(--ds-border)" }}>
                    {bulkUpdateResult.skipped.map((s, i) => (
                      <div key={i} className="py-1 border-b last:border-b-0" style={{ borderColor: "var(--ds-border)" }}>
                        <b>Riga {s.row}:</b> {s.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUpdateOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------------- Log Magazzino --------------------
function LogTab() {
  const [movs, setMovs] = useState([]);
  const [products, setProducts] = useState([]);
  const [deposits, setDeposits] = useState([]);

  const load = async () => {
    const [m, p, d] = await Promise.all([
      api.get("/movements"),
      api.get("/products"),
      api.get("/deposits"),
    ]);
    setMovs(m.data);
    setProducts(p.data);
    setDeposits(d.data);
  };
  useEffect(() => { load(); }, []);

  const productName = (id) => products.find((p) => p.id === id)?.nome_articolo || "—";
  const depositName = (id) => deposits.find((d) => d.id === id)?.nome || "—";

  const { toggleSort, SortIcon, sortedRows } = useSort(movs, {
    initial: { by: "created_at", dir: "desc" },
    getKey: (row, by) => {
      if (by === "prodotto") return productName(row.prodotto_id).toLowerCase();
      if (by === "deposito") return depositName(row.deposito_id).toLowerCase();
      if (by === "quantita") return Number(row.quantita) || 0;
      return row[by];
    },
  });

  return (
    <div className="space-y-4">
      <p className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
        Tutti i movimenti di carico/scarico nei tuoi depositi.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("created_at")} data-testid="sort-log-data">Data<SortIcon col="created_at" /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("tipo")} data-testid="sort-log-tipo">Tipo<SortIcon col="tipo" /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("prodotto")} data-testid="sort-log-prodotto">Prodotto<SortIcon col="prodotto" /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("deposito")} data-testid="sort-log-deposito">Deposito<SortIcon col="deposito" /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("quantita")} data-testid="sort-log-qta">Quantità<SortIcon col="quantita" /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("utente_nome")} data-testid="sort-log-utente">Utente<SortIcon col="utente_nome" /></TableHead>
            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("note")} data-testid="sort-log-note">Note<SortIcon col="note" /></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessun movimento</TableCell></TableRow>}
          {sortedRows.map((m) => (
            <TableRow key={m.id} data-testid={`mov-row-${m.id}`}>
              <TableCell className="text-sm">{new Date(m.created_at).toLocaleString("it-IT")}</TableCell>
              <TableCell>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium"
                  style={{ color: m.tipo === "carico" ? "#7B8B6F" : "#8B3A36" }}>
                  {m.tipo === "carico" ? <ArrowDownCircle size={14} /> : <ArrowUpCircle size={14} />}
                  {m.tipo}
                </span>
              </TableCell>
              <TableCell className="text-sm font-medium">{productName(m.prodotto_id)}</TableCell>
              <TableCell className="text-sm">{depositName(m.deposito_id)}</TableCell>
              <TableCell className="font-medium">{m.quantita}</TableCell>
              <TableCell className="text-xs">{m.utente_nome || "—"}</TableCell>
              <TableCell className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>{m.note || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// -------------------- Reset dati --------------------
function ResetDataTab() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const doReset = async () => {
    if (confirmText !== "RESET") {
      toast.error('Digita "RESET" per confermare');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset");
      setLastResult(data.deleted);
      toast.success("Tutte le tabelle sono state svuotate");
      setOpen(false);
      setConfirmText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-lg border" style={{ borderColor: "var(--ds-danger)", background: "rgba(139,58,54,0.04)" }}>
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} style={{ color: "var(--ds-danger)" }} className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-primary font-semibold text-base" style={{ color: "var(--ds-danger)" }}>
              Zona pericolosa
            </div>
            <p className="text-sm mt-1" style={{ color: "var(--ds-text-secondary)" }}>
              Svuota <b>tutte le tabelle dei dati operativi</b>: prodotti, movimenti, storici prezzi, ordini,
              fornitori, depositi, unità di misura e categorie. <br />
              Utenti e ruoli vengono conservati. <b>Operazione irreversibile.</b>
            </p>
            <Button
              onClick={() => setOpen(true)}
              variant="outline"
              className="mt-4 rounded-lg"
              style={{ borderColor: "var(--ds-danger)", color: "var(--ds-danger)" }}
              data-testid="reset-data-button"
            >
              <Trash2 size={14} className="mr-1.5" /> Svuota tutte le tabelle
            </Button>
          </div>
        </div>
      </div>

      {lastResult && (
        <div className="ds-card p-4">
          <div className="label-tag mb-2">Ultimo reset</div>
          <div className="text-sm grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(lastResult).map(([k, v]) => (
              <div key={k} className="flex justify-between border rounded-lg px-3 py-2" style={{ borderColor: "var(--ds-border)" }}>
                <span style={{ color: "var(--ds-text-secondary)" }}>{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent data-testid="reset-data-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light flex items-center gap-2">
              <AlertTriangle size={20} style={{ color: "var(--ds-danger)" }} /> Confermi il reset?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2 text-sm">
            <p style={{ color: "var(--ds-text-secondary)" }}>
              Verranno cancellati <b>definitivamente</b> tutti i dati di prodotti, movimenti,
              ordini, fornitori, depositi, unità di misura e categorie. Utenti e ruoli sono preservati.
            </p>
            <div>
              <Label className="label-tag">Digita "RESET" per confermare</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-1.5 rounded-lg font-mono"
                placeholder="RESET"
                data-testid="reset-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button
              onClick={doReset}
              disabled={busy || confirmText !== "RESET"}
              style={{ background: "var(--ds-danger)" }}
              data-testid="reset-confirm-button"
            >
              {busy ? "Reset in corso…" : "Sì, svuota tutto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------------- Main Page --------------------
export default function Impostazioni() {
  const hasPerm = useHasPerm();
  const canEdit = hasPerm("impostazioni:edit");
  return (
    <div className="space-y-6" data-testid="impostazioni-page">
      <div>
        <div className="label-tag mb-2">Configurazione</div>
        <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">Impostazioni</h1>
        <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
          Gestisci depositi, unità di misura, categorie e consulta il log magazzino.
        </p>
      </div>

      <Tabs defaultValue="depositi">
        <TabsList className="bg-white border flex-wrap h-auto" style={{ borderColor: "var(--ds-border)" }}>
          <TabsTrigger value="depositi" data-testid="tab-depositi">Depositi</TabsTrigger>
          <TabsTrigger value="unita" data-testid="tab-unita">Unità di misura</TabsTrigger>
          <TabsTrigger value="categorie" data-testid="tab-categorie">Categorie</TabsTrigger>
          {canEdit && <TabsTrigger value="utenti" data-testid="tab-utenti">Utenti</TabsTrigger>}
          {canEdit && <TabsTrigger value="ruoli" data-testid="tab-ruoli">Ruoli</TabsTrigger>}
          {canEdit && <TabsTrigger value="danger" data-testid="tab-danger">Reset dati</TabsTrigger>}
        </TabsList>
        <TabsContent value="depositi" className="ds-card p-6 mt-4"><DepositiTab /></TabsContent>
        <TabsContent value="unita" className="ds-card p-6 mt-4"><UnitsTab /></TabsContent>
        <TabsContent value="categorie" className="ds-card p-6 mt-4"><CategoriesTab /></TabsContent>
        {canEdit && <TabsContent value="utenti" className="ds-card p-6 mt-4"><UtentiTab /></TabsContent>}
        {canEdit && <TabsContent value="ruoli" className="ds-card p-6 mt-4"><RuoliTab /></TabsContent>}
        {canEdit && <TabsContent value="danger" className="ds-card p-6 mt-4"><ResetDataTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
